// The Mirabilis run's evidence cards as the report shows them. The server attaches them to
// `report.evidence_cards` in dossier order; the E-NN label is that order, as the Observatory
// numbers them, so the two views agree.

import { visualOf } from "./visualSpec.js";
import { strengthWord } from "../report/evidenceStrength.js";

// A card with no title line (a prefetched page excerpt) arrives as one paragraph in `title`.
// Past this length it is shown as a clipped first sentence, with the full text as the body.
const TITLE_MAX = 120;
// A first "sentence" shorter than this is an abbreviation ("The U.S."), not a sentence.
const MIN_SENTENCE = 30;

// A card's text ends with its "Source: <url>" line, which the card's rail already shows,
// often under a "Quotes:" heading left empty.
const TRAILING_SOURCE = /(?:\s*Quotes:\s*)?\s*Source:\s*https?:\/\/\S+\s*$/i;
// A quick run's page excerpt ends with this capture note, which says nothing to a reader.
const EXTRACTION_NOTE = /\s*\[quick extraction truncated at \d+ characters\]\s*$/i;

// The forecasting system's own vocabulary, which a card written by one of its agents can carry
// ("Chain-hunter synthesis: …", "(root researcher, calendar mechanics)", "[researcher-2-004]").
// None of it tells a reader anything about the evidence.
// Each term is one only the system uses, so evidence about Gemini, researchers, scouts or
// Python reads untouched.
const SYSTEM_TERM =
  /\b(chain-hunters?|(?:root|sub-?|lead) researchers?|researchers?-\d+|run_python|deep-research|synthesi[sz]ers?-\d+|scouts?-\d+|scout notes?|harness (?:run|trace|workspace)|gemini[ -](?:\d|flash|pro\b)|turn-\d+|job[-_#]\d+|\w+\.py|challenger (?:synthesis|sweep|audit|review))\b/i;
// A title that opens on the agent itself: "Chain-hunter independent rebuild of …".
const SYSTEM_SUBJECT = /^(?:the\s+)?(?:chain-hunters?|root researchers?)(?:'s)?\s+/i;
// "[researcher-2-004]", "[cite 3]", and the research tree's section numbers "[1.4.2, 1.1.5]".
const INTERNAL_REF =
  /\s*`?\[(?:(?:[a-z]+-)+\d+(?:-\d+)*|cite \d+|\d+(?:\.\d+){2,}(?:,\s*\d+(?:\.\d+){2,})*)\]`?/gi;
const SYSTEM_PARENTHETICAL = /\s*\([^()]*\)/g;
// A leading "who/what produced this" label, up to the first colon or dash.
const SYSTEM_PREFIX = /^([^:—]{0,140})(:|\s—)\s*/;

/** Card text with the system's own references removed; a title is also cut clear of them. */
export function readerCardText(text, { title = false } = {}) {
  let t = String(text || "")
    .replace(INTERNAL_REF, "")
    .replace(SYSTEM_PARENTHETICAL, (m) => (SYSTEM_TERM.test(m) ? "" : m));
  if (title) {
    const prefix = t.match(SYSTEM_PREFIX);
    if (prefix && SYSTEM_TERM.test(prefix[1]) && t.length - prefix[0].length >= MIN_SENTENCE)
      t = t.slice(prefix[0].length);
    t = t.replace(SYSTEM_SUBJECT, "");
    // Still naming the system: end the title at the clause break before the mention.
    const at = t.search(SYSTEM_TERM);
    if (at >= 0) {
      const cut = Math.max(t.lastIndexOf(",", at), t.lastIndexOf(" —", at), t.lastIndexOf(";", at));
      if (cut >= MIN_SENTENCE) t = t.slice(0, cut);
    }
    t = t.trim().replace(/[,;—\s]+$/, "");
    t = t.charAt(0).toUpperCase() + t.slice(1);
  }
  return t.replace(/[ \t]{2,}/g, " ").trim();
}

/** The first sentence of `text`, clipped at a word boundary to TITLE_MAX characters. */
function clipTitle(text) {
  const first = text.match(/^.+?[.!?](?=\s|$)/)?.[0];
  const sentence = first && first.length >= MIN_SENTENCE ? first : text;
  if (sentence.length <= TITLE_MAX) return sentence;
  const cut = sentence.slice(0, TITLE_MAX);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 60 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * The report's evidence cards, labelled E1, E2…; malformed entries are dropped.
 * @param {object|null} report the report object (`r`)
 * A card's `title` is the server's one-line headline when it wrote one, and `sourceTitle` the
 * title the card arrived with.
 * @returns {Array<{label: string, cardId: string, title: string, sourceTitle: string, body: string, sourceUrl: string, publishedAt: string, reliability: number|null, reliabilityWhy: string, angle: string}>}
 */
export function evidenceCardsOf(report) {
  const raw = Array.isArray(report?.evidence_cards) ? report.evidence_cards : [];
  return raw
    .filter((c) => c && typeof c === "object" && typeof c.title === "string" && c.title.trim())
    .map((c, i) => {
      // Only a real title is cut clear of system mentions; a page excerpt arriving in `title`
      // (a card with no body) keeps its text and loses just the references.
      const hasBody = typeof c.body === "string" && !!c.body.trim();
      const text = readerCardText(c.title.trim().replace(EXTRACTION_NOTE, ""), { title: hasBody });
      const body =
        typeof c.body === "string"
          ? readerCardText(c.body.replace(EXTRACTION_NOTE, "").replace(TRAILING_SOURCE, ""))
          : "";
      const untitled = !body.trim() && text.length > TITLE_MAX;
      const sourceTitle = untitled ? clipTitle(text) : text;
      const headline = typeof c.headline === "string" ? readerCardText(c.headline).trim() : "";
      return {
        label: `E${i + 1}`,
        cardId: typeof c.card_id === "string" ? c.card_id : "",
        title: headline || sourceTitle,
        sourceTitle,
        body: untitled ? text : body,
        sourceUrl: /^https?:\/\//.test(c.source_url || "") ? c.source_url : "",
        publishedAt: typeof c.published_at === "string" ? c.published_at : "",
        reliability: Number.isInteger(c.reliability) ? c.reliability : null,
        reliabilityWhy: typeof c.reliability_why === "string" ? c.reliability_why.trim() : "",
        angle: typeof c.angle === "string" ? c.angle.trim() : "",
        // The server's one- or two-sentence summary of the card's key evidence, when present.
        summary: typeof c.summary === "string" ? readerCardText(c.summary) : "",
        // A chart of the card's own figures, when the server built one (visualSpec.js).
        visual: visualOf(c.visual),
        // The card's single decisive figure, when the server picked one: {value, label}.
        keyStat:
          c.key_stat && typeof c.key_stat.value === "string" && typeof c.key_stat.label === "string"
            ? { value: c.key_stat.value.trim(), label: c.key_stat.label.trim() }
            : null,
      };
    });
}

/**
 * The cards a reason cites, in citation order. A Mirabilis reason cites cards by id (bare
 * strings) alongside or instead of `{source_url, quote}` objects; ids with no card on the
 * report are dropped, as are repeats.
 * @param {Array|null} citations a reason's `citations`
 * @param {ReturnType<typeof evidenceCardsOf>} cards the report's labelled cards
 * @returns {ReturnType<typeof evidenceCardsOf>}
 */
export function citedCardsOf(citations, cards) {
  const byId = new Map(cards.map((c) => [c.cardId, c]));
  const out = [];
  for (const id of citations || []) {
    const card = typeof id === "string" ? byId.get(id) : null;
    if (card && !out.includes(card)) out.push(card);
  }
  return out;
}

// Two links to the same page: the scheme, "www.", the host's case and a trailing slash
// aside. The path and query keep their case, which can tell two pages apart.
const samePage = (url) => {
  const raw = String(url || "").trim();
  try {
    const u = new URL(raw);
    const host = u.host.toLowerCase().replace(/^www\./, "");
    return `${host}${u.pathname.replace(/\/+$/, "")}${u.search}`;
  } catch {
    return raw.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/+$/, "");
  }
};

/**
 * Everything a reason cites, as the reader follows it: the evidence cards (cited by id, or by
 * a link to the page a card was made from) and the other sources it links to directly, each
 * once, in citation order. A Quick report cites by `{source_url, quote}` and a Deep one by
 * card id, so both resolve here.
 * @param {Array|null} citations a reason's `citations`
 * @param {ReturnType<typeof evidenceCardsOf>} cards the report's labelled cards
 * @returns {{cards: ReturnType<typeof evidenceCardsOf>, sources: Array<{url: string, quote: string}>}}
 */
export function reasonReferencesOf(citations, cards) {
  const byId = new Map((cards || []).map((c) => [c.cardId, c]));
  // Only a card with an id can be opened from a reference, and only a page one card was made
  // from names it: a link to an id-less card's page, or to a page several cards share, stays a
  // source link.
  const byPage = new Map();
  for (const c of cards || []) {
    if (!c.cardId || !c.sourceUrl) continue;
    const page = samePage(c.sourceUrl);
    byPage.set(page, byPage.has(page) ? null : c);
  }
  const out = { cards: [], sources: [] };
  const seenPages = new Set();
  for (const cit of citations || []) {
    if (typeof cit === "string") {
      const card = byId.get(cit);
      if (card && !out.cards.includes(card)) out.cards.push(card);
      continue;
    }
    const url = typeof cit?.source_url === "string" ? cit.source_url.trim() : "";
    if (!/^https?:\/\//i.test(url)) continue;
    const card = byPage.get(samePage(url));
    const quote = typeof cit.quote === "string" ? cit.quote.trim() : "";
    if (card) {
      if (!out.cards.includes(card)) out.cards.push(card);
    } else if (!seenPages.has(samePage(url))) {
      seenPages.add(samePage(url));
      out.sources.push({ url, quote, quotes: quote ? [quote] : [] });
    } else if (quote) {
      // The same page cited again for another passage: its link previews both.
      const src = out.sources.find((s) => samePage(s.url) === samePage(url));
      if (src && !src.quotes.includes(quote)) src.quotes.push(quote);
    }
  }
  return out;
}

/** A card's key for navigation: its id, or its label ("E2") when it has none. */
export const evidenceCardRef = (card) => card?.cardId || card?.label || "";

/** A card's name on its own face: "E17" reads "Evidence 17". */
export const evidenceCardName = (card) =>
  String(card?.label || "").replace(/^E(?=\d)/, "Evidence ");

/** The DOM id a card's evidence-board entry carries, for a reason's chip to scroll to. */
export const evidenceCardAnchor = (cardId) => `evidence-card-${cardId}`;

/** A reason's source citations — the `{source_url}` objects, without its card ids. */
export function sourceCitationsOf(citations) {
  return (citations || []).filter((c) => c && typeof c === "object");
}

/**
 * The reasons citing each card, as their on-screen badge ("Y1", "N3") and claim.
 * @param {{left: {prefix: string, items: Array}, right: {prefix: string, items: Array}}|null} columns
 *   the report's evidence columns (selectEvidenceColumns), exactly as the screen shows them
 * @param {ReturnType<typeof evidenceCardsOf>} [cards] the report's cards, to match reasons that
 *   cite a card's page by link rather than by id
 * @returns {Map<string, Array<{ref: string, claim: string}>>} card id → citing reasons, in column order
 */
export function citationsByCard(columns, cards = []) {
  const out = new Map();
  for (const side of [columns?.left, columns?.right]) {
    (side?.items || []).forEach((item, i) => {
      const ref = `${side.prefix}${i + 1}`;
      // Cited by id, or by a link to the page a card was made from (as a Quick report cites).
      const ids = [
        ...(item.citations || []).filter((id) => typeof id === "string"),
        // A card without an id has no key of its own here, so it is never credited by link.
        ...reasonReferencesOf(item.citations, cards)
          .cards.map((c) => c.cardId)
          .filter(Boolean),
      ];
      for (const id of ids) {
        const refs = out.get(id) || [];
        if (!refs.some((r) => r.ref === ref))
          refs.push({ ref, claim: item.claim || "", strength: item.strength });
        out.set(id, refs);
      }
    });
  }
  return out;
}

// A figure in prose or a table cell: "138k", "58.9%", "234,171", "$4.2B". Not part of a
// word ("Q4", "H1") or of a larger number.
// A leading minus is the figure's sign ("-3.5%", "(−274K)"); in a range ("41-59%") it follows
// a digit, so it is a dash and both ends stay positive.
const FIGURE =
  /(?<![\w.,])([-−]?)(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?(?:\s?(%|k\b|K\b|m\b|M\b|bn\b|B\b))?(?![\d,]*\d)/g;
const SCALE = { k: 1e3, K: 1e3, m: 1e6, M: 1e6, bn: 1e9, B: 1e9 };

function figureAt(match) {
  const [, sign, whole, frac = "", suffix = ""] = match;
  const scale = SCALE[suffix] || 1;
  const value = (sign ? -1 : 1) * Number(whole.replace(/,/g, "") + frac) * scale;
  // An abbreviation stands for whatever rounds to it: "25k" is 24,500–25,499.
  const tolerance = suffix in SCALE ? 0.5 * scale * 10 ** -Math.max(frac.length - 1, 0) : 0;
  // A bare integer identifies something only past 99 and outside the years, which (like
  // small counts) match everywhere.
  const bare = !frac && !suffix && !whole.includes(",");
  const informative = !bare || (Math.abs(value) >= 100 && !(value >= 1900 && value <= 2100));
  return { value, tolerance, informative };
}

/** The informative figures a reason quotes, for finding them in its card. */
export function quotedFiguresOf(claim) {
  return [...String(claim || "").matchAll(FIGURE)].map(figureAt).filter((f) => f.informative);
}

/** Whether `value` is one of the quoted figures, an abbreviation to its own precision. */
function matchesQuoted(value, figures) {
  return figures.some((f) => Math.abs(value - f.value) <= f.tolerance);
}

/**
 * `text` split into runs, marking the figures a reason quotes.
 * @returns {Array<{text: string, hit: boolean}>}
 */
export function markQuotedFigures(text, figures) {
  const s = String(text ?? "");
  if (!figures?.length) return [{ text: s, hit: false }];
  const runs = [];
  let at = 0;
  for (const m of s.matchAll(FIGURE)) {
    const f = figureAt(m);
    if (!f.informative || !matchesQuoted(f.value, figures)) continue;
    if (m.index > at) runs.push({ text: s.slice(at, m.index), hit: false });
    runs.push({ text: m[0], hit: true });
    at = m.index + m[0].length;
  }
  if (at < s.length) runs.push({ text: s.slice(at), hit: false });
  return runs;
}

/** Whether a table cell holds a quoted figure. */
export function cellQuoted(cell, figures) {
  return markQuotedFigures(cell, figures).some((r) => r.hit);
}

/**
 * The table cells holding the figures a reason quotes, as "row:col" keys. Per figure only
 * its closest cells count, so "25k" marks 25,219 rather than every value rounding to it.
 * @param {{rows: string[][]}} table
 */
export function quotedCellsOf(table, figures) {
  const keys = new Set();
  if (!figures?.length) return keys;
  const cells = [];
  table.rows.forEach((row, i) =>
    row.forEach((cell, j) => {
      if (!j) return;
      for (const m of String(cell ?? "").matchAll(FIGURE)) {
        const f = figureAt(m);
        if (f.informative) cells.push({ key: `${i}:${j}`, value: f.value });
      }
    }),
  );
  for (const q of figures) {
    const near = cells.filter((c) => Math.abs(c.value - q.value) <= q.tolerance);
    const best = Math.min(...near.map((c) => Math.abs(c.value - q.value)));
    for (const c of near) if (Math.abs(c.value - q.value) === best) keys.add(c.key);
  }
  return keys;
}

// The front of a flip card: at most this many characters of summary.
const SUMMARY_MAX = 260;
// A paragraph longer than this, with no breaks of its own, is split into readable chunks.
const PARAGRAPH_MAX = 480;
const SENTENCES_PER_PARAGRAPH = 3;

// A word whose full stop is not a sentence's end: initials ("U.S.", "J.") and common short
// forms ("Dr.", "Inc.", "Sept.", "e.g.", "incl.", "Fig.").
const ABBREVIATION =
  /(?:\b(?:[A-Z]\.){1,3}|\b(?:Mr|Mrs|Ms|Dr|Prof|Inc|Ltd|Corp|Co|St|Jr|Sr|vs|etc|approx|est|incl|Fig|No|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec|e\.g|i\.e)\.)$/;

/** Sentences of `text`, keeping each one's closing punctuation; abbreviations end none. */
function sentencesOf(text) {
  const t = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  const out = [];
  let start = 0;
  for (const m of t.matchAll(/[.!?]+(?=\s|$)/g)) {
    const end = m.index + m[0].length;
    if (end < t.length && ABBREVIATION.test(t.slice(start, end))) continue;
    out.push(t.slice(start, end).trim());
    start = end;
  }
  if (start < t.length) out.push(t.slice(start).trim());
  return out.filter(Boolean);
}

// A summary is read at a glance, so its sentences must read as prose. The bounds below are
// strict: missing a summary costs less than showing a line of data. Figures are no more than
// this share of a sentence's words (one figure is always allowed),
const PROSE_FIGURE_SHARE = 0.25;
// and it has none of a working note's notation: "= 97.6%", "13% -> 16%", ">= 2.5", "x1.458",
// "~$2,400", "a; b; c", "AR/ID/KY", a table row "| Sector | Status |", or a short label it
// opens on ("Safe R: …", "CAVEAT: …"),
const NOTE_NOTATION = /[=<>≥≤→~;|]|\bx\d|\b\w+\/\w+\/\w+\b|^(?:\S+\s){0,2}\S+:\s/;
// nor two asides of figures: "(median 2.65, printed ~72), B (median 2.52)".
const FIGURE_ASIDE = /\([^()]*\d[^()]*\)/g;
// A year reads as a date, not a figure.
const YEAR = /^\(?(?:19|20)\d\d[).,:]*$/;

/** Whether a sentence reads as prose, not as a researcher's line of data. */
function readsAsProse(sentence) {
  const s = String(sentence || "");
  // A leftover scrap of a split line (", Ltd. All rights reserved.", "going back to 1950'
  // and …") is no sentence at all.
  if (/^[,;:.)\]'’a-z-]/.test(s) || /all rights reserved/i.test(s)) return false;
  if (NOTE_NOTATION.test(s) || SYSTEM_TERM.test(s) || (s.match(FIGURE_ASIDE) || []).length > 1)
    return false;
  // A figure starts with its number ("$4.2B", "(90", "+2.5C"); a label like "Q2" or "FY2027"
  // is a word.
  const tokens = s.split(/\s+/);
  const isFigure = (t) => /^[(~$€£+±−-]*\d/.test(t);
  const figures = tokens.filter((t) => isFigure(t) && !YEAR.test(t)).length;
  const words = tokens.filter((t) => !isFigure(t) && /[A-Za-z]/.test(t)).length;
  return words > 0 && figures <= Math.max(1, words * PROSE_FIGURE_SHARE);
}

/**
 * A card's key evidence in one or two sentences: the server's summary when the report
 * carries one; otherwise the first of its "Key facts" bullets, or else of its sentences, that
 * reads as prose, and the next one when it does too. A card with no body summarises its title
 * text. A card whose every line is data has no summary.
 * @param {{title: string, body: string}} card an evidenceCardsOf entry
 * @returns {string}
 */
export function cardSummaryOf(card) {
  // The server writes its summary as prose, figures and all, and it is shown as written.
  if (typeof card?.summary === "string" && card.summary.trim()) return card.summary.trim();
  const body = String(card?.body || "");
  const facts = body
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => /^[-•*]\s+/.test(l))
    .map((l) => l.replace(/^[-•*]\s+/, ""));
  const sentences = facts.length
    ? // A fact without a full stop gets one, so two facts never run together.
      facts.flatMap((f) => sentencesOf(/[.!?]["'”’)]*$/.test(f) ? f : `${f}.`))
    : sentencesOf(
        (
          body
            .replace(/^\s*(Key facts|Quotes):\s*$/gim, "")
            // A quoted line reads as its words; the quote marker is no comparison.
            .replace(/^[ \t]*>[ \t]?/gm, "")
            .trim() ||
          card?.title ||
          ""
        )
          // A line break is a sentence break, even where the line carries no full stop.
          .replace(/([^.!?:;\s])[ \t]*\n+\s*/g, "$1. "),
      );
  const at = sentences.findIndex(readsAsProse);
  if (at < 0) return "";
  const summary = (
    readsAsProse(sentences[at + 1]) ? `${sentences[at]} ${sentences[at + 1]}` : sentences[at]
  )
    .replace(/\s+/g, " ")
    .trim();
  if (summary.length <= SUMMARY_MAX) return summary;
  const cut = summary.slice(0, SUMMARY_MAX);
  const space = cut.lastIndexOf(" ");
  return `${(space > SUMMARY_MAX * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/**
 * `text` as readable paragraphs: its own blank-line breaks kept, and any long unbroken run
 * split every few sentences. Lists, quotes and tables (lines starting "-", ">" or "|") pass
 * through untouched, so card content rendering still recognises them.
 * @param {string} text
 * @returns {string} paragraphs separated by blank lines
 */
export function paragraphsOf(text) {
  return String(text || "")
    .split(/\n\s*\n/)
    .flatMap((para) => {
      const p = para.trim();
      if (!p || p.length <= PARAGRAPH_MAX || /\n/.test(p) || /^[-•*>|]/.test(p)) return [p];
      const sentences = sentencesOf(p);
      const out = [];
      for (let i = 0; i < sentences.length; i += SENTENCES_PER_PARAGRAPH)
        out.push(sentences.slice(i, i + SENTENCES_PER_PARAGRAPH).join(" "));
      return out;
    })
    .filter(Boolean)
    .join("\n\n");
}

export const RELIABILITY_HELP =
  "Source trust, 1 (unverified or secondhand) to 5 (official or primary).";

const STRENGTH_POINTS = { weak: 1, moderate: 2, strong: 3 };

export const RELIANCE_HELP =
  "How much the forecast's insights lean on this card: the strength of each insight citing it, weighted by the source's reliability. A ranking, not a measured effect on the probability.";

/**
 * The evidence ordered by how much the forecast relies on it. A cited card scores the strength
 * of each insight citing it (weak 1, moderate 2, strong 3; unrated counts as moderate), scaled
 * by its source's reliability out of 5 (unrated as 3), and is labelled against the
 * best-supported card: High from two thirds of its score, Medium from a third, else Low. The
 * cards no insight cites follow as background, most reliable first. Ties keep dossier order.
 *
 * @param {Array} cards evidenceCardsOf(r)
 * @param {Map} citations citationsByCard(columns)
 * @returns {{cited: Array<{card: object, score: number, level: "High"|"Medium"|"Low"}>, background: Array}}
 */
export function rankByReliance(cards, citations) {
  const scored = cards.map((card, i) => {
    const uses = citations?.get(card.cardId) || [];
    const points = uses.reduce(
      (sum, u) => sum + (STRENGTH_POINTS[strengthWord(u.strength)] || 2),
      0,
    );
    return { card, i, score: (points * (card.reliability ?? 3)) / 5, cited: uses.length > 0 };
  });
  const top = Math.max(0, ...scored.map((s) => s.score));
  const cited = scored
    .filter((s) => s.cited)
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map(({ card, score }) => ({
      card,
      score,
      level: score >= (top * 2) / 3 ? "High" : score >= top / 3 ? "Medium" : "Low",
    }));
  const background = scored
    .filter((s) => !s.cited)
    .sort((a, b) => (b.card.reliability ?? 0) - (a.card.reliability ?? 0) || a.i - b.i)
    .map((s) => s.card);
  return { cited, background };
}
